import { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import {
  Box,
  Container,
  FormControlLabel,
  IconButton,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import DataTable, { TableColumn } from "react-data-table-component";
import { tableCustomStyles } from "../../utils/table-styles";
import { MdAdd, MdArchive } from "react-icons/md";
import { BsBucketFill } from "react-icons/bs";
import { toast } from "react-toastify";

import { useAppDispatch, useAppSelector } from "../../hooks";
import { history } from "../..";
import PageHero from "../shared/page-hero";
import {
  clearStockpile,
  clearContainmentPad,
  getStockpiles,
  getContainmentPads,
  setStockpiles,
  setContainmentPads,
} from "../../store/storageAreaStore";
import { Stockpile } from "../../interface/api/Stockpile";
import { ContainmentPad } from "../../interface/api/ContainmentPad";
import storageAreaService from "../../services/storageAreaService";
import { SetStockpilesAction } from "../../interface/redux-types/SetStockpilesAction";
import { SetContainmentPadsAction } from "../../interface/redux-types/SetContainmentPadsAction";
import DeleteDialog from "../shared/dialog/delete";
import PaginatedTableTop, { paginationTopClassName } from "../shared/paginated-table-top";
import { ActionIconGroup, DeleteIcon, EditIcon } from "../shared/icons";
import { TabPanel } from "../shared/tab-panel";
import { a11yProps } from "../../utils/constants";
import { formatDateTime } from "../../utils/date-helper";

const useStyles = makeStyles({
  addIconButton: {
    border: "1px solid rgba(0, 0, 0, 0.23)",
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
    margin: "1rem 0 0.5rem",
  },
  toolbarLeft: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
  },
  search: {
    minWidth: 260,
  },
  "@keyframes pulse": {
    "0%": { transform: "scale(1)", opacity: 1 },
    "50%": { transform: "scale(1.2)", opacity: 0.55 },
    "100%": { transform: "scale(1)", opacity: 1 },
  },
  pulse: {
    animation: "$pulse 1.4s ease-in-out infinite",
  },
});

const StorageAreasView = ({}) => {
  const classes = useStyles();

  const stockpiles = useAppSelector(getStockpiles);
  const containmentPads = useAppSelector(getContainmentPads);
  const dispatch = useAppDispatch();

  const [tab, setTab] = useState<number>(() => {
    const saved = Number(localStorage.getItem("storageAreasTab"));
    return saved === 1 ? 1 : 0;
  });

  const onChangeTab = (v: number) => {
    setTab(v);
    localStorage.setItem("storageAreasTab", String(v));
  };
  const [stockpileSearch, setStockpileSearch] = useState("");
  const [padSearch, setPadSearch] = useState("");
  const [stockpileShowClosed, setStockpileShowClosed] = useState(false);
  const [padShowClosed, setPadShowClosed] = useState(false);

  const [openDeleteStockpile, setOpenDeleteStockpile] = useState(false);
  const [openDeleteContainmentPad, setOpenDeleteContainmentPad] =
    useState(false);
  const [selectedStockpile, setSelectedStockpile] = useState<Partial<Stockpile>>(
    {}
  );
  const [selectedContainmentPad, setSelectedContainmentPad] = useState<
    Partial<ContainmentPad>
  >({});

  const onCloseDeleteStockpile = () => setOpenDeleteStockpile(false);
  const onCloseDeleteContainmentPad = () => setOpenDeleteContainmentPad(false);

  useMemo(() => {
    storageAreaService.getStockpiles().then((response) => {
      if (response.ok) {
        dispatch(setStockpiles({ stockpiles: response.data as Stockpile[] }));
      }
    });
    storageAreaService.getContainmentPads().then((response) => {
      if (response.ok) {
        dispatch(
          setContainmentPads({
            containmentPads: response.data as ContainmentPad[],
          })
        );
      }
    });
  }, []);

  const refreshStockpiles = () => {
    storageAreaService.getStockpiles().then((response) => {
      if (response.ok) {
        dispatch(setStockpiles({ stockpiles: response.data as Stockpile[] }));
      }
    });
  };

  // Live-refresh the load counts when the user returns to this tab (e.g. after
  // receiving loads elsewhere). Event-driven on focus — no polling.
  useEffect(() => {
    const onFocus = () => refreshStockpiles();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  const onCloseForSampling = (id: number) => {
    storageAreaService.closeStockpileForSampling(id).then((response) => {
      if (response.ok) {
        toast.success("Stockpile closed for sampling");
        refreshStockpiles();
      } else {
        toast.error((response.data as string) || "Failed to close for sampling");
      }
    });
  };

  const refreshContainmentPads = () => {
    storageAreaService.getContainmentPads().then((response) => {
      if (response.ok) {
        dispatch(
          setContainmentPads({
            containmentPads: response.data as ContainmentPad[],
          })
        );
      }
    });
  };

  const onCloseContainmentPadForSampling = (id: number) => {
    storageAreaService.closeContainmentPadForSampling(id).then((response) => {
      if (response.ok) {
        toast.success("Containment pad closed for sampling");
        refreshContainmentPads();
      } else {
        toast.error((response.data as string) || "Failed to close for sampling");
      }
    });
  };

  const onConfirmDeleteStockpile = () => {
    if (selectedStockpile && selectedStockpile.id) {
      storageAreaService
        .deleteStockpile(selectedStockpile.id)
        .then((response) => {
          if (response.ok) {
            dispatch(
              setStockpiles({
                stockpiles: stockpiles.filter(
                  (s) => s.id !== selectedStockpile.id
                ),
              })
            );
          }
        });
    }
    onCloseDeleteStockpile();
  };

  const onConfirmDeleteContainmentPad = () => {
    if (selectedContainmentPad && selectedContainmentPad.id) {
      storageAreaService
        .deleteContainmentPad(selectedContainmentPad.id)
        .then((response) => {
          if (response.ok) {
            dispatch(
              setContainmentPads({
                containmentPads: containmentPads.filter(
                  (c) => c.id !== selectedContainmentPad.id
                ),
              })
            );
          }
        });
    }
    onCloseDeleteContainmentPad();
  };

  // ----- Filtering (search + active/closed toggle) -----

  // Newest approvals first when viewing the Closed list.
  const byApprovalDesc = (
    a: { approvalDate?: string | null },
    b: { approvalDate?: string | null }
  ) =>
    new Date(b.approvalDate ?? 0).getTime() -
    new Date(a.approvalDate ?? 0).getTime();

  const filteredStockpiles = useMemo(() => {
    const q = stockpileSearch.trim().toLowerCase();
    const rows = stockpiles.filter((s) => {
      const matchClosed = stockpileShowClosed
        ? s.status === "Closed"
        : s.status !== "Closed";
      const matchSearch =
        !q ||
        (s.name ?? "").toLowerCase().includes(q) ||
        (s.status ?? "").toLowerCase().includes(q) ||
        String(s.id).includes(q);
      return matchClosed && matchSearch;
    });
    return stockpileShowClosed ? [...rows].sort(byApprovalDesc) : rows;
  }, [stockpiles, stockpileSearch, stockpileShowClosed]);

  const filteredPads = useMemo(() => {
    const q = padSearch.trim().toLowerCase();
    const rows = containmentPads.filter((c) => {
      const matchClosed = padShowClosed
        ? c.status === "Closed"
        : c.status !== "Closed";
      const matchSearch =
        !q ||
        (c.name ?? "").toLowerCase().includes(q) ||
        (c.status ?? "").toLowerCase().includes(q) ||
        String(c.id).includes(q);
      return matchClosed && matchSearch;
    });
    return padShowClosed ? [...rows].sort(byApprovalDesc) : rows;
  }, [containmentPads, padSearch, padShowClosed]);

  // ----- Columns -----

  // Colored pill so Active vs. the sampling/closed states read at a glance.
  const statusChip = (status: string) => {
    const palette: Record<string, { bg: string; fg: string }> = {
      Active: { bg: "#e6f4ea", fg: "#1e7e34" },
      "To Be Sampled": { bg: "#fff4e5", fg: "#b26a00" },
      Pending: { bg: "#fff4e5", fg: "#b26a00" },
      Closed: { bg: "#eceff1", fg: "#546e7a" },
    };
    const c = palette[status] ?? { bg: "#eceff1", fg: "#546e7a" };
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "2px 10px",
          borderRadius: 12,
          fontSize: 12,
          fontWeight: 600,
          background: c.bg,
          color: c.fg,
          whiteSpace: "nowrap",
        }}
      >
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: c.fg,
            flexShrink: 0,
          }}
        />
        {status || "—"}
      </span>
    );
  };

  const stockpileColumns: TableColumn<Stockpile>[] = [
    { name: "ID", selector: (r) => r.id, sortable: true, width: "70px" },
    { name: "Name", selector: (r) => r.name ?? "", sortable: true },
    {
      name: "Status",
      selector: (r) => r.status ?? "",
      sortable: true,
      cell: (r) => statusChip(r.status ?? ""),
    },
    {
      name: "Loads",
      width: "100px",
      cell: (r) => {
        const lc = r.loadCount ?? 0;
        const full = lc >= r.capacity;
        return (
          <span
            style={{
              color: full ? "#c62828" : undefined,
              fontWeight: full ? 600 : undefined,
            }}
          >
            {lc} / {r.capacity}
          </span>
        );
      },
    },
    { name: "Capacity", selector: (r) => r.capacity, width: "100px" },
    {
      name: "Approval",
      minWidth: "260px",
      cell: (r) =>
        r.status === "Closed" ? (
          <Box
            display="flex"
            alignItems="center"
            style={{ gap: 8, paddingRight: 16 }}
          >
            {r.inspectorSignature && (
              <img
                src={r.inspectorSignature}
                alt="QP signature"
                style={{
                  height: 30,
                  width: "auto",
                  maxWidth: 80,
                  flexShrink: 0,
                  objectFit: "contain",
                  background: "#fff",
                  border: "1px solid rgba(0,0,0,0.15)",
                  borderRadius: 4,
                }}
              />
            )}
            <span style={{ whiteSpace: "nowrap" }}>
              {r.approvalDate
                ? formatDateTime(r.approvalDate, true)
                : ""}
            </span>
          </Box>
        ) : (
          <span>—</span>
        ),
    },
    {
      name: "Actions",
      width: "180px",
      cell: (r) => {
        const loadCount = r.loadCount ?? 0;
        const isFull = loadCount >= r.capacity;
        const isActive = r.status === "Active";
        const isToBeSampled = r.status === "To Be Sampled";
        const isPending = r.status === "Pending";
        const isClosed = r.status === "Closed";
        const needsSampling = isToBeSampled || isPending;
        return (
          <ActionIconGroup spread>
            {isActive && isFull && (
              <Tooltip title="Close for Sampling">
                <IconButton
                  size="small"
                  className={classes.pulse}
                  style={{ color: "#ef6c00" }}
                  onClick={() => onCloseForSampling(r.id)}
                >
                  <MdArchive size={20} />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip
              title={
                isPending
                  ? "Finish Sampling"
                  : isToBeSampled
                  ? "Ready to Sample"
                  : "View Loads"
              }
            >
              <IconButton
                size="small"
                color={needsSampling ? "default" : "primary"}
                className={needsSampling ? classes.pulse : undefined}
                style={needsSampling ? { color: "#2e7d32" } : undefined}
                onClick={() => history.push(`/stockpile-sampling/${r.id}`)}
              >
                <BsBucketFill size={needsSampling ? 20 : 18} />
              </IconButton>
            </Tooltip>
            <EditIcon onClick={() => history.push(`/stockpile/${r.id}`)} />
            {!isClosed && (
              <DeleteIcon
                onClick={() => {
                  setSelectedStockpile(r);
                  setOpenDeleteStockpile(true);
                }}
              />
            )}
          </ActionIconGroup>
        );
      },
    },
  ];

  const padColumns: TableColumn<ContainmentPad>[] = [
    { name: "ID", selector: (r) => r.id, sortable: true, width: "70px" },
    { name: "Name", selector: (r) => r.name ?? "", sortable: true },
    {
      name: "Status",
      selector: (r) => r.status ?? "",
      sortable: true,
      cell: (r) => statusChip(r.status ?? ""),
    },
    {
      name: "Loads",
      width: "100px",
      cell: (r) => {
        const lc = r.loadCount ?? 0;
        const full = lc >= r.capacity;
        return (
          <span
            style={{
              color: full ? "#c62828" : undefined,
              fontWeight: full ? 600 : undefined,
            }}
          >
            {lc} / {r.capacity}
          </span>
        );
      },
    },
    { name: "Capacity", selector: (r) => r.capacity, width: "100px" },
    {
      name: "Approval",
      minWidth: "260px",
      cell: (r) =>
        r.status === "Closed" ? (
          <Box
            display="flex"
            alignItems="center"
            style={{ gap: 8, paddingRight: 16 }}
          >
            {r.inspectorSignature && (
              <img
                src={r.inspectorSignature}
                alt="QP signature"
                style={{
                  height: 30,
                  width: "auto",
                  maxWidth: 80,
                  flexShrink: 0,
                  objectFit: "contain",
                  background: "#fff",
                  border: "1px solid rgba(0,0,0,0.15)",
                  borderRadius: 4,
                }}
              />
            )}
            <span style={{ whiteSpace: "nowrap" }}>
              {r.approvalDate
                ? formatDateTime(r.approvalDate, true)
                : ""}
            </span>
          </Box>
        ) : (
          <span>—</span>
        ),
    },
    {
      name: "Actions",
      width: "180px",
      cell: (r) => {
        const isActive = r.status === "Active";
        const isToBeSampled = r.status === "To Be Sampled";
        const isClosed = r.status === "Closed";
        return (
          <ActionIconGroup spread>
            {isActive && (
              <Tooltip title="Close for Sampling">
                <IconButton
                  size="small"
                  className={classes.pulse}
                  style={{ color: "#ef6c00" }}
                  onClick={() => onCloseContainmentPadForSampling(r.id)}
                >
                  <MdArchive size={20} />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip
              title={isToBeSampled ? "Ready to Sample" : "View Loads"}
            >
              <IconButton
                size="small"
                color={isToBeSampled ? "default" : "primary"}
                className={isToBeSampled ? classes.pulse : undefined}
                style={isToBeSampled ? { color: "#2e7d32" } : undefined}
                onClick={() =>
                  history.push(`/containment-pad-sampling/${r.id}`)
                }
              >
                <BsBucketFill size={isToBeSampled ? 20 : 18} />
              </IconButton>
            </Tooltip>
            <EditIcon
              onClick={() => history.push(`/containment-pad/${r.id}`)}
            />
            {!isClosed && (
              <DeleteIcon
                onClick={() => {
                  setSelectedContainmentPad(r);
                  setOpenDeleteContainmentPad(true);
                }}
              />
            )}
          </ActionIconGroup>
        );
      },
    },
  ];

  return (
    <>
      <Container maxWidth="xl">
        <PageHero
          title="Storage Areas"
          subtitle="Add and Manage Stockpiles and Containment Pads."
          showBackButton={false}
        />

        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Tabs
            value={tab}
            onChange={(_e, v) => onChangeTab(v)}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab label="Stockpiles" {...a11yProps(0)} />
            <Tab label="Containment Pads" {...a11yProps(1)} />
          </Tabs>
          <Tooltip title={tab === 0 ? "Add Stockpile" : "Add Containment Pad"}>
            <IconButton
              color="primary"
              className={classes.addIconButton}
              onClick={() => {
                if (tab === 0) {
                  dispatch(clearStockpile());
                  history.push(`/stockpile/new`);
                } else {
                  dispatch(clearContainmentPad());
                  history.push(`/containment-pad/new`);
                }
              }}
            >
              <MdAdd size={24} />
            </IconButton>
          </Tooltip>
        </Box>

        <TabPanel value={tab} index={0}>
          <div className={classes.toolbar}>
            <div className={classes.toolbarLeft}>
              <TextField
                className={classes.search}
                variant="outlined"
                label="Search"
                name="stockpileSearch"
                value={stockpileSearch}
                onChange={(e) => setStockpileSearch(e.target.value)}
              />
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    checked={stockpileShowClosed}
                    onChange={(e) => setStockpileShowClosed(e.target.checked)}
                  />
                }
                label={
                  <span style={{ whiteSpace: "nowrap" }}>
                    {stockpileShowClosed ? "Closed" : "Active"}
                  </span>
                }
              />
            </div>
          </div>
          <PaginatedTableTop>
            <DataTable
              customStyles={tableCustomStyles}
              className={paginationTopClassName}
              columns={stockpileColumns}
              data={filteredStockpiles}
              pagination
              paginationPerPage={25}
              paginationRowsPerPageOptions={[25, 50, 100, 200]}
              highlightOnHover
              striped
              responsive
              noDataComponent="No stockpiles."
            />
          </PaginatedTableTop>
        </TabPanel>

        <TabPanel value={tab} index={1}>
          <div className={classes.toolbar}>
            <div className={classes.toolbarLeft}>
              <TextField
                className={classes.search}
                variant="outlined"
                label="Search"
                name="padSearch"
                value={padSearch}
                onChange={(e) => setPadSearch(e.target.value)}
              />
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    checked={padShowClosed}
                    onChange={(e) => setPadShowClosed(e.target.checked)}
                  />
                }
                label={
                  <span style={{ whiteSpace: "nowrap" }}>
                    {padShowClosed ? "Closed" : "Active"}
                  </span>
                }
              />
            </div>
          </div>
          <PaginatedTableTop>
            <DataTable
              customStyles={tableCustomStyles}
              className={paginationTopClassName}
              columns={padColumns}
              data={filteredPads}
              pagination
              paginationPerPage={25}
              paginationRowsPerPageOptions={[25, 50, 100, 200]}
              highlightOnHover
              striped
              responsive
              noDataComponent="No containment pads."
            />
          </PaginatedTableTop>
        </TabPanel>
      </Container>
      <DeleteDialog
        open={openDeleteStockpile}
        onClose={onCloseDeleteStockpile}
        onConfirmDelete={onConfirmDeleteStockpile}
      />
      <DeleteDialog
        open={openDeleteContainmentPad}
        onClose={onCloseDeleteContainmentPad}
        onConfirmDelete={onConfirmDeleteContainmentPad}
      />
    </>
  );
};

export default connect()(StorageAreasView);
